import { Button } from '@/components/common/button'
import { Separator } from '@/components/common/separator'
import { LoadingIcon } from '@/components/icons/loading'
import { useMixpanel } from '@/context/mixpanel'
import { useBatchSupported } from '@/hooks/useBatchSupported'
import type { StepData } from '@/types/transactionsContext'
import { cn } from '@/utils/cn'
import type { Network } from '@vaultsfyi/common/types'
import { type VariantProps, cva } from 'class-variance-authority'
import { Fragment, useEffect, useState } from 'react'
import type { Address } from 'viem'
import { useTxSubmissionBlocked } from '../txPanel/txSubmissionGuard'

interface TxStepsProps {
  account: Address | undefined
  stepData: StepData | undefined
  activeStep: number
  loading: boolean
  disabled?: boolean
  label: string
  network: Network
  success?: boolean
}

export const TxSteps = ({
  account,
  stepData,
  activeStep,
  loading,
  disabled,
  label,
  network,
  success,
}: TxStepsProps) => {
  const { checkBatchSupported } = useBatchSupported(network)
  const [isBatchSupported, setIsBatchSupported] = useState(false)

  useEffect(() => {
    checkBatchSupported().then(setIsBatchSupported)
  }, [checkBatchSupported])

  const isSingleTransaction = !!stepData && (stepData.actions.length === 1 || isBatchSupported)

  if (!stepData || isSingleTransaction || !account) {
    return <TxButton disabled={disabled} loading={loading} label={label} />
  }

  const isTxButtonDisabled = disabled || !!success

  return (
    <div className={cn('flex flex-col gap-3 md:gap-4', 'rounded-xl border bg-page p-2 md:p-3')}>
      <div className="flex w-full justify-center">
        {stepData.actions.map((_, index) => {
          const stepLabel = index === stepData.actions.length - 1 ? label : stepData.actions[index]
          return (
            <Fragment key={index}>
              <TxStep
                stepIndex={index}
                currentStep={activeStep}
                loading={loading}
                label={stepLabel}
                success={success}
              />
              {index < stepData.actions.length - 1 && (
                <Separator
                  className={cn(
                    '-translate-y-1/2 -mx-4 mt-5 h-[2px] w-16',
                    (index < activeStep || success) && 'bg-accent'
                  )}
                />
              )}
            </Fragment>
          )
        })}
      </div>
      <TxButton disabled={isTxButtonDisabled} loading={loading} label={label} />
    </div>
  )
}

const stepIndicatorVariants = cva(
  cn('flex size-10 min-w-10 items-center justify-center rounded-full border-2 font-bold'),
  {
    variants: {
      variant: {
        past: 'border-accent bg-accent text-page dark:text-heading-foreground',
        current: cn(
          'relative',
          'before:-top-0.5 before:-left-0.5 before:absolute before:size-10 before:rounded-full before:border-2 before:border-accent before:opacity-0 before:transition-all before:[clip-path:polygon(0_0,_50%_0,_50%_100%,_0%_100%)]'
        ),
        next: '',
        success: 'border-accent bg-accent text-page dark:text-heading-foreground',
      },
    },
    defaultVariants: {
      variant: 'next',
    },
  }
)

interface TxStepProps extends Pick<TxStepsProps, 'loading' | 'label' | 'success'> {
  stepIndex: number
  currentStep: number
}

type StepVariants = VariantProps<typeof stepIndicatorVariants>['variant']

function TxStep({ stepIndex, currentStep, label, loading, success }: TxStepProps) {
  const stepPosition: StepVariants = success
    ? 'success'
    : stepIndex < currentStep
      ? 'past'
      : stepIndex === currentStep
        ? 'current'
        : 'next'

  return (
    <div className="group relative flex flex-col items-center gap-2 px-4 pb-7" data-step={stepPosition}>
      <div className="relative flex w-full items-center justify-center">
        <div
          className={stepIndicatorVariants({
            variant: stepPosition,
            className: loading && 'before:animate-spin before:opacity-100',
          })}
        >
          {stepIndex + 1}
        </div>
      </div>
      <span className="absolute bottom-0 inline-flex whitespace-nowrap text-center text-sm capitalize">{label}</span>
    </div>
  )
}

const TxButton = ({ disabled, loading, label }: Pick<TxStepsProps, 'disabled' | 'loading' | 'label'>) => {
  const { trackEvent } = useMixpanel()
  const submissionBlocked = useTxSubmissionBlocked()

  return (
    <Button
      type="submit"
      className="h-13 gap-2 capitalize"
      disabled={disabled || loading || submissionBlocked}
      onClick={() => trackEvent('Opportunity widget action clicked', { Action: label })}
    >
      {loading && <LoadingIcon size={16} className="animate-spin" />}
      <span>{label}</span>
    </Button>
  )
}
